/*!
 * SoundVerdict — ONE owner of "is there sound in this file, and will a listener hear it".
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * /repair-audio judged the CONTAINER and nothing else: header, timestamps,
 * frames. People bring it a different question — "I cannot hear it". When the
 * container was intact the page answered "your file appears healthy" and, if
 * asked, "Repair Complete" — whatever the reason for the silence.
 *
 * Measured on the live page 2026-10-10 with twelve fixtures:
 *   mp3 / wav / mp4 holding digital silence → "healthy", "Remove silence →" on a
 *       file that is ONLY silence, then "Repair Complete!" and the day's file spent
 *   video with no audio track → "Audio codec not recognized", "Channels: Mono",
 *       "Processing error. Try a different output format or re-upload your file"
 *   music at −45 dBFS → "100 % silence"
 *   AC-3 / E-AC-3 / DTS in mp4 / mkv → "healthy — try the denoiser"
 * and in production, 30 days to 2026-10-10: a 62-minute, 685 MB WAV of zeros
 * handed back as "Repair complete"; "structurally healthy" was the answer to
 * 62 % of the presses on the RU half and 41 % on the intl one.
 *
 * ── What it is NOT ──────────────────────────────────────────────────────────
 * It measures nothing. Every number comes from an owner that already exists
 * and was calibrated on real recordings:
 *   level     TakeLevel.analyze        (take-level.js)       levelDb, peakDb
 *   channels  StereoContent.analyse    (stereo-content.js)   one-sided, polarity
 *   track     MediaAudioProbe.probe    (media-audio-probe.js) hasAudio, codec
 * A second copy of a measurement is how two answers about the same sound start
 * to disagree. This module only decides what the facts MEAN to a listener.
 *
 * ── Contract ────────────────────────────────────────────────────────────────
 *   fromBuffer(audioBuffer)  → { level, stereo, evidence:'decode' }   (needs TakeLevel, StereoContent)
 *   fromPeaks(peaks)         → { level, stereo, evidence:'pcm' }      (WaveformPreview.peaksFromPcm result)
 *   judge({ probe, level, stereo, evidence, tier })
 *       → { state, findings:[{id,…}], withdraw, proven }
 *
 *   state     the first finding, or 'audible' (measured, nothing found), or
 *             'unknown' (nothing measured, or the evidence may not be trusted)
 *   findings  every reason a listener may hear nothing, most decisive first:
 *     no_track     the container has no audio stream at all
 *     silent       the track holds no sound       {peakDb, digital}
 *     codec        the sound is there, in a codec browsers and many phones
 *                  do not decode                  {codec}
 *     faint        the sound is there, far below a normal level {needDb, levelDb}
 *     one_side     only one of the two channels carries sound   {side}
 *     mono_cancel  the channels cancel on a single speaker      {lossDb}
 *   withdraw  true when there is nothing a repair could return: the caller
 *             takes the launch offer away and puts the verdict in its place
 *   proven    true when the silence was READ (PCM bytes, a container that
 *             lists no audio stream) rather than decoded
 *
 * ── Silence is a strong claim ───────────────────────────────────────────────
 * "Nothing was recorded" may make a person delete the file. It is said only
 * on evidence that cannot be a decoder's artefact:
 *   • PCM read straight from the bytes, or
 *   • a decode by the browser's own decoder (tier < 2) of a codec that decoder
 *     really handles. A native decode of mp4 holding AC-3 / ALAC can come back
 *     as zeros (media-audio-probe.js, measured 2026-08-05), and tier 2 means
 *     the browser REFUSED the file and FFmpeg wrote what it could.
 * Anything else answers 'unknown' and the page behaves as it did before.
 *
 * Guards: tests/js/sound-verdict.test.mjs, RepairAudioAnswersIsThereSoundTest,
 * tests/playwright/repair-audio-is-there-sound.mjs.
 */
(function (root) {
    'use strict';

    /* None of these is new. Each is the value its owner already uses, repeated
       here by NAME so a change there is a decision here too (the unit test
       compares them against the owners' exports). */
    var FLOOR_DB = -66;        // TakeLevel.DEFAULTS.minLevelDb — "below this there is no voice to raise"
    var TARGET_DB = -9;        // TakeLevel.DEFAULTS.targetDb — the quiet end of a normal recording
    var DIGITAL_DB = -100;     // /dictaphone DEAD_DBFS — a dead input reads −180, a live silent room −66…−79
    var FAINT_NEED_DB = 18;    // /dictaphone QUIET_BOOST_DB — from here a take is offered a boost
    var DEAD_SIDE_DB = -60;    // StereoContent QUIET_CH_DB — one channel effectively dead

    function dbOf(x) { return x > 1e-9 ? 20 * Math.log10(x) : -180; }
    function r1(x) { return (typeof x === 'number' && isFinite(x)) ? Math.round(x * 10) / 10 : null; }

    /** Facts from a decoded buffer. null when an owner module is missing. */
    function fromBuffer(buffer) {
        if (!buffer || !root || !root.TakeLevel) return null;
        var st = null, sc = null;
        try { st = root.TakeLevel.analyze(buffer); } catch (_) { st = null; }
        if (!st || !st.frames) return null;
        try { sc = root.StereoContent ? root.StereoContent.analyse(buffer) : null; } catch (_) { sc = null; }
        return {
            evidence: 'decode',
            level: { peakDb: st.peakDb, levelDb: st.levelDb, durationS: st.durationS },
            stereo: sc ? {
                kind: sc.kind, loudSide: sc.loudSide || null,
                polarityFlipped: !!sc.polarityFlipped, monoSumSafe: sc.monoSumSafe !== false,
                sumLossDb: sc.sumLossDb
            } : null
        };
    }

    /**
     * Facts from PCM peaks (no decoder was involved — the bytes ARE the samples).
     * `peaks` is what WaveformPreview.peaksFromPcm returns: one min/max pair per
     * column, plus `chPeak` (absolute peak of every channel over the whole scan).
     * The level is the 98th percentile of the column peaks — the same statistic
     * TakeLevel takes over 50 ms frames, on coarser frames, so it can only read
     * LOUDER than TakeLevel would: a quiet file may be missed, a normal one is
     * never called quiet.
     */
    function fromPeaks(peaks) {
        if (!peaks || !peaks.mins || !peaks.maxs || !peaks.mins.length) return null;
        var n = peaks.mins.length, cols = new Array(n), peak = 0;
        for (var i = 0; i < n; i++) {
            var a = Math.max(Math.abs(peaks.mins[i]), Math.abs(peaks.maxs[i]));
            if (a > peak) peak = a;
            cols[i] = dbOf(a);
        }
        cols.sort(function (x, y) { return x - y; });
        var levelDb = cols[Math.min(n - 1, Math.max(0, Math.round((n - 1) * 0.98)))];
        var stereo = null, cp = peaks.chPeak;
        if (cp && cp.length === 2) {
            var l = dbOf(cp[0]), r = dbOf(cp[1]);
            var dead = Math.min(l, r) - Math.max(l, r) < DEAD_SIDE_DB;
            stereo = { kind: dead ? 'one-sided' : 'stereo', loudSide: dead ? (l >= r ? 'left' : 'right') : null,
                       polarityFlipped: false, monoSumSafe: true, sumLossDb: 0 };
        }
        return { evidence: 'pcm', level: { peakDb: dbOf(peak), levelDb: levelDb, durationS: peaks.duration || 0 }, stereo: stereo };
    }

    function judge(f) {
        f = f || {};
        var probe = f.probe || null, level = f.level || null, stereo = f.stereo || null;
        var out = { state: 'unknown', findings: [], withdraw: false, proven: false, level: level };

        // A container that was READ and lists no audio stream. `hasAudio:null`
        // is "could not tell" and must never arrive here as "none".
        if (probe && probe.hasAudio === false) {
            out.findings.push({ id: 'no_track' });
            out.state = 'no_track'; out.withdraw = true; out.proven = true;
            return out;
        }

        var refused = !!(probe && probe.playable === false);

        if (level && typeof level.peakDb === 'number') {
            /* Silence is judged on the LOUDEST moment, never on the level: the
               level is a 98th percentile, and an 81-minute recording that speaks
               for 22 seconds (RU, 2026-10-06) has a level of −180 dBFS and a
               voice in it. Only a file whose peak is under the floor is silent. */
            if (level.peakDb < FLOOR_DB) {
                var read = f.evidence === 'pcm';
                var trusted = read || (f.evidence === 'decode' && !refused && !(f.tier >= 2));
                if (!trusted) { out.reason = 'untrusted_silence'; return out; }
                out.findings.push({ id: 'silent', peakDb: r1(level.peakDb), digital: level.peakDb <= DIGITAL_DB });
                out.state = 'silent'; out.withdraw = true; out.proven = read;
                return out;
            }
            if (refused) out.findings.push({ id: 'codec', codec: String(probe.reason || (probe.codecs && probe.codecs[0]) || '') });
            /* How far the sound sits under a normal recording. The level when it
               describes the sound; the peak when most of the file is silence and
               the level describes the silence instead. */
            var loud = (typeof level.levelDb === 'number' && level.levelDb >= FLOOR_DB) ? level.levelDb : level.peakDb;
            var need = TARGET_DB - loud;
            if (need >= FAINT_NEED_DB) out.findings.push({ id: 'faint', needDb: Math.round(need), levelDb: r1(loud) });
            if (stereo && stereo.kind === 'one-sided' && stereo.loudSide) {
                out.findings.push({ id: 'one_side', side: stereo.loudSide });
            } else if (stereo && stereo.polarityFlipped && stereo.monoSumSafe === false) {
                out.findings.push({ id: 'mono_cancel', lossDb: Math.round(Math.abs(stereo.sumLossDb || 0)) });
            }
            out.state = out.findings.length ? out.findings[0].id : 'audible';
            return out;
        }

        // Nothing was measured (file over the decode budget, decoder refused):
        // the codec is still a fact the header gave us.
        if (refused) {
            out.findings.push({ id: 'codec', codec: String(probe.reason || (probe.codecs && probe.codecs[0]) || '') });
            out.state = 'codec';
        }
        return out;
    }

    var api = {
        fromBuffer: fromBuffer,
        fromPeaks: fromPeaks,
        judge: judge,
        THRESHOLDS: { FLOOR_DB: FLOOR_DB, TARGET_DB: TARGET_DB, DIGITAL_DB: DIGITAL_DB,
                      FAINT_NEED_DB: FAINT_NEED_DB, DEAD_SIDE_DB: DEAD_SIDE_DB }
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.SoundVerdict = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : null));
